
import Vue from 'vue'
import { Company } from '@/types/Company'
import axios, { AxiosError } from 'axios'
import { EventBus } from '@/plugins/eventBus'
import { state } from '@/plugins/state'

type CompanyWithIndex = Company & { [key: string]: unknown }

export default Vue.extend({
  name: 'CompanyForm',
  props: {
    company: {
      type: Object as () => Company,
      required: true
    }
  },
  data(): { 
    localCompany: CompanyWithIndex
    valid: boolean
    apeCodeSearch: string
  } {
    // Create a deep copy of the company
    const companyCopy = JSON.parse(JSON.stringify(this.company)) as CompanyWithIndex
    
    // Set trade_name to company name if it doesn't exist or is empty
    if (!companyCopy.trade_name || companyCopy.trade_name.trim() === '') {
      companyCopy.trade_name = companyCopy.name || ''
    }
    
    return {
      localCompany: companyCopy,
      valid: false,
      apeCodeSearch: ''
    }
  },
  computed: {
    companyStates(): string[] {
      return ['prospect', 'active', 'inactive', 'suspended', 'closed']
    },
    companyCategories(): string[] {
      return ['TPE', 'PME', 'ETI', 'GE']
    },
    legalForms(): Array<{ text: string; value: string }> {
      // Access the legal_form_codes from the current locale
      const currentLocale = this.$vuetify.lang.current
      const localeData = this.$vuetify.lang.locales[currentLocale]
      const codes = localeData?.legal_form_codes

      return Object.entries(codes).map(([code, description]) => ({
        value: code,
        text: description as string
      })).sort((a, b) => a.text.localeCompare(b.text))
    },
    apeNafOptions(): Array<{ text: string; value: string }> {
      // Access the ape_naf_codes from the current locale
      const currentLocale = this.$vuetify.lang.current
      const localeData = this.$vuetify.lang.locales[currentLocale]
      const codes = localeData?.ape_naf_codes
      
      // Only keep codes that match the valid search pattern:
      // pattern matches APE/NAF codes in format: 2 digits . 2 digits and 1 uppercase letter (e.g., '47.51Z')
      const validCodeRegex = /^\d{2}\.\d{2}[A-Z]$/
      
      return Object.entries(codes)
        .filter(([code]) => validCodeRegex.test(code))
        .map(([code, description]) => ({
          value: code,
          text: `${description} (${code})`
        }))
        .sort((a, b) => a.value.localeCompare(b.value))
    },
    employeeCountOptions(): Array<{ text: string; value: string }> {
      // Access the employee_count_codes from the current locale
      const currentLocale = this.$vuetify.lang.current
      const localeData = this.$vuetify.lang.locales[currentLocale]
      const codes = localeData?.employee_count_codes

      return Object.entries(codes).map(([code, description]) => ({
        value: code,
        text: description as string
      })).sort((a, b) => {
        // Sort by the numeric value of the code (e.g., "00", "01", "02", etc.)
        // Handle special case "NN" (non-employer) which should come first
        if (a.value === 'NN') return -1
        if (b.value === 'NN') return 1
        return a.value.localeCompare(b.value)
      })
    },
    companyNameRules(): Array<(v: any) => boolean | string> {
      return [(v: any) => !!v || this.$vuetify.lang.t('$vuetify.company_name_required')]
    },
    companyTradeNameRules(): Array<(v: any) => boolean | string> {
      return [(v: any) => !!v || this.$vuetify.lang.t('$vuetify.trade_name_required')]
    },
    sirenRules(): Array<(v: any) => boolean | string> {
      return [(v: any) => !v || v.length === 9 || this.$vuetify.lang.t('$vuetify.siren_must_be_9_digits')]
    },
    siretRules(): Array<(v: any) => boolean | string> {
      return [(v: any) => !v || v.length === 14 || this.$vuetify.lang.t('$vuetify.siret_must_be_14_digits')]
    }
  },
  methods: {
    removeManager(idx: number): void {
      if (this.localCompany.managers) {
        this.localCompany.managers.splice(idx, 1)
      }
    },
    async saveCompany(): Promise<void> {
      if (this.$refs.form && (this.$refs.form as any).validate()) {
        // Clean up the data before saving
        const companyData = { ...this.localCompany }
        
        // Remove display-only fields
        delete companyData.manager_name

        // Prepare the request body with space_id
        const requestBody = {
          company: {
            space_id: state.agency ? parseInt(state.agency) : 1, // Default to 1 if no space selected
            name: companyData.name,
            trade_name: companyData.trade_name ?? null,
            complete_name: companyData.complete_name ?? null,
            state: companyData.state ?? null,
            siret: companyData.siret ?? null,
            siren: companyData.siren ?? null,
            category: companyData.category ?? null,
            legal_form: companyData.legal_form ?? null,
            ape_code: companyData.ape_code ?? null,
            activity_details: companyData.activity_details ?? null,
            prefix: companyData.prefix ?? null,
            address: companyData.address ?? null,
            postal_code: companyData.postal_code ?? null,
            city: companyData.city ?? null,
            country: companyData.country ?? null,
            head_office_address: companyData.head_office_address ?? null,
            billing_address: companyData.billing_address ?? null,
            phone: companyData.phone ?? null,
            phone_unformatted: companyData.phone_unformatted ?? null,
            website: companyData.website ?? null,
            intra_community_vat_code: companyData.intra_community_vat_code ?? null,
            code_idcc: companyData.code_idcc ?? null,
            code_idcc_label: companyData.code_idcc_label ?? null,
            annual_revenue_k: companyData.annual_revenue_k ?? null,
            employee_count: companyData.employee_count ?? null,
            etp_count: companyData.etp_count ?? null,
            external_ref: companyData.external_ref ?? null,
            is_foreign: companyData.is_foreign ?? false,
            is_group: companyData.is_group ?? false,
            projects: companyData.projects ?? null,
            flexibility_management: companyData.flexibility_management ?? null,
            comment: companyData.comment ?? null
          }
        }

        // Make API call to create company
        axios.post('/v1/company', requestBody)
          .then(async (response) => {
            const companyId = response.data
            
            // Create contacts if managers exist
            if (companyData.managers && companyData.managers.length > 0) {
              const contactsBody = companyData.managers
                .filter(manager => manager.prenoms || manager.nom) // Only include managers with names
                .map(manager => ({
                  first_name: manager.prenoms || null,
                  last_name: manager.nom || null,
                  company_id: companyId.toString()
                }))
              
              if (contactsBody.length > 0) {
                axios.post('/v1/contacts', contactsBody)
                .catch((contactError: AxiosError) => {
                  EventBus.$emit('snackbar', {axiosError: contactError})
                })
              }
            }
            
            // Show success message
            EventBus.$emit('snackbar', {
              message: this.$vuetify.lang.t('$vuetify.company_successfully_created'),
              color: 'success'
            })
            // Emit the cleaned data for parent component
            this.$emit('save', companyData)
            // Close the modal after successful creation
            this.$emit('close')
          })
          .catch((error: AxiosError) => {
            // Handle error with snackbar
            EventBus.$emit('snackbar', { axiosError: error })
          })
      }
    }
  }
})
